<template>
  <div class="kv-ve-mobile-bar-root" @click.stop>
    <!-- Collapsed state (nothing selected) -->
    <button
      v-if="!open && !pending"
      type="button"
      class="kv-ve-mobile-fab"
      :aria-label="$_('visualEditor-emailEditor-mobileInsertBar-add')"
      :title="$_('visualEditor-emailEditor-mobileInsertBar-add')"
      @click="$emit('update:open', true)"
    >
      <span class="mdi mdi-plus" aria-hidden="true"></span>
    </button>

    <!-- Collapsed state (pending placement): show a compact + (re-open) and X (cancel) -->
    <div v-else-if="!open && pending" class="kv-ve-mobile-mini">
      <button
        type="button"
        class="kv-ve-mobile-fab kv-ve-mobile-fab--active"
        :aria-label="$_('visualEditor-emailEditor-mobileInsertBar-chooseOther')"
        :title="$_('visualEditor-emailEditor-mobileInsertBar-chooseOther')"
        @click="$emit('update:open', true)"
      >
        <span class="mdi mdi-plus" aria-hidden="true"></span>
      </button>
      <button
        type="button"
        class="kv-ve-mobile-fab kv-ve-mobile-fab--cancel"
        :aria-label="$_('visualEditor-emailEditor-mobileInsertBar-cancelInsertion')"
        :title="$_('visualEditor-emailEditor-mobileInsertBar-cancelInsertion')"
        @click="$emit('cancel')"
      >
        <span class="mdi mdi-close" aria-hidden="true"></span>
      </button>
    </div>

    <!-- Expanded palette -->
    <div
      v-else
      class="kv-ve-mobile-bar"
      :class="{
        'kv-ve-mobile-bar--modules': mode === 'modules',
        'kv-ve-mobile-bar--has-pending': Boolean(pending),
      }"
      role="toolbar"
      :aria-label="$_('visualEditor-emailEditor-mobileInsertBar-insertContent')"
    >
      <div class="kv-ve-mobile-bar__inner">
        <div class="kv-ve-mobile-bar__top" :aria-label="$_('visualEditor-emailEditor-mobileInsertBar-mode')">
          <button
            type="button"
            class="kv-ve-mobile-bar__mode-btn kv-ve-mobile-bar__mode-btn--close"
            :title="$_('visualEditor-emailEditor-mobileInsertBar-close')"
            :aria-label="$_('visualEditor-emailEditor-mobileInsertBar-close')"
            @click="$emit('update:open', false)"
          >
            <span class="mdi mdi-chevron-down" aria-hidden="true"></span>
          </button>

          <div class="kv-ve-mobile-bar__mode-group" role="group" :aria-label="$_('visualEditor-emailEditor-mobileInsertBar-selectMode')">
            <button
              type="button"
              class="kv-ve-mobile-bar__mode-btn"
              :class="{ active: mode === 'blocks' }"
              :title="$_('visualEditor-emailEditor-tabs-blocks')"
              :aria-label="$_('visualEditor-emailEditor-tabs-blocks')"
              @click="$emit('update:mode', 'blocks')"
            >
              <span class="mdi mdi-format-text" aria-hidden="true"></span>
            </button>
            <button
              type="button"
              class="kv-ve-mobile-bar__mode-btn"
              :class="{ active: mode === 'layout' }"
              :title="$_('visualEditor-emailEditor-tabs-layout')"
              :aria-label="$_('visualEditor-emailEditor-tabs-layout')"
              @click="$emit('update:mode', 'layout')"
            >
              <span class="mdi mdi-view-dashboard-outline" aria-hidden="true"></span>
            </button>
            <button
              type="button"
              class="kv-ve-mobile-bar__mode-btn"
              :class="{ active: mode === 'modules' }"
              :title="$_('visualEditor-emailEditor-tabs-modules')"
              :aria-label="$_('visualEditor-emailEditor-tabs-modules')"
              @click="$emit('update:mode', 'modules')"
            >
              <span class="mdi mdi-view-grid-plus-outline" aria-hidden="true"></span>
            </button>
          </div>

          <button
            v-if="pending"
            type="button"
            class="kv-ve-mobile-bar__mode-btn kv-ve-mobile-bar__mode-btn--cancel"
            :title="$_('visualEditor-emailEditor-mobileInsertBar-cancel')"
            :aria-label="$_('visualEditor-emailEditor-mobileInsertBar-cancel')"
            @click="$emit('cancel')"
          >
            <span class="mdi mdi-close" aria-hidden="true"></span>
          </button>
        </div>

        <div class="kv-ve-mobile-bar__content" :aria-label="$_('visualEditor-emailEditor-mobileInsertBar-elements')">
          <!-- Blocks -->
          <div v-if="mode === 'blocks'" class="kv-ve-mobile-bar__icons">
            <button
              v-for="b in blocks"
              :key="b.type"
              type="button"
              class="kv-ve-mobile-bar__icon-btn"
              :class="{ active: pending && pending.kind === 'block' && pending.type === b.type }"
              :title="b.title"
              :aria-label="b.title"
              @click="$emit('select', { kind: 'block', type: b.type })"
            >
              <span v-if="b.iconMdi" class="mdi" :class="b.iconMdi" aria-hidden="true"></span>
              <span v-else class="kv-ve-mobile-bar__fallback-icon" aria-hidden="true">{{ b.fallback || '•' }}</span>
            </button>
          </div>

          <!-- Layout (rows) -->
          <div v-else-if="mode === 'layout'" class="kv-ve-mobile-bar__icons kv-ve-mobile-bar__icons--layout">
            <button
              v-for="r in rows"
              :key="r.key"
              type="button"
              class="kv-ve-mobile-bar__icon-btn kv-ve-mobile-bar__row-btn"
              :class="{ active: pending && pending.kind === 'row' && pending.key === r.key }"
              :title="$_('visualEditor-emailEditor-mobileInsertBar-row', [r.caption])"
              :aria-label="$_('visualEditor-emailEditor-mobileInsertBar-row', [r.caption])"
              @click="$emit('select', { kind: 'row', key: r.key })"
            >
              <span class="kv-ve-mobile-bar__row-caption" aria-hidden="true">{{ r.caption }}</span>
            </button>
          </div>

          <!-- Modules -->
          <div v-else class="kv-ve-mobile-bar__modules">
            <div v-if="modulesLoading" class="kv-ve-mobile-bar__modules-empty">{{ $_('visualEditor-emailEditor-mobileInsertBar-loading') }}</div>
            <div v-else-if="!modules || !modules.length" class="kv-ve-mobile-bar__modules-empty">
              {{ $_('visualEditor-emailEditor-mobileInsertBar-noModules') }}
            </div>
            <div v-else class="kv-ve-mobile-bar__modules-scroll" role="list">
              <button
                v-for="m in modules"
                :key="m.id"
                type="button"
                class="kv-ve-mobile-bar__module-card"
                :class="{ active: pending && pending.kind === 'module' && pending.id === m.id }"
                role="listitem"
                :title="m.name || $_('visualEditor-emailEditor-mobileInsertBar-module')"
                :aria-label="m.name || $_('visualEditor-emailEditor-mobileInsertBar-module')"
                @click="$emit('select', { kind: 'module', id: m.id })"
              >
                <div class="kv-ve-mobile-bar__module-thumb">
                  <img v-if="m.screenshot" :src="m.screenshot" :alt="m.name || $_('visualEditor-emailEditor-mobileInsertBar-module')" loading="lazy" />
                  <div v-else class="kv-ve-mobile-bar__module-thumb-fallback">{{ $_('visualEditor-emailEditor-mobileInsertBar-noPreview') }}</div>
                </div>
                <div class="kv-ve-mobile-bar__module-name">{{ m.name || $_('visualEditor-emailEditor-mobileInsertBar-module') }}</div>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "MobileInsertBar",
  props: {
    open: { type: Boolean, default: false },
    mode: { type: String, default: "blocks" }, // blocks | layout | modules
    pending: { type: Object, default: null },
    blocks: { type: Array, default: () => [] },
    rows: { type: Array, default: () => [] },
    modules: { type: Array, default: () => [] },
    modulesLoading: { type: Boolean, default: false },
  },
};
</script>

<style scoped lang="scss">
.kv-ve-mobile-bar-root {
  position: fixed;
  left: 50%;
  transform: translateX(-50%);
  bottom: 10px;
  width: 95vw;
  max-width: 680px;
  z-index: 2800; /* above canvas selection UI, below preview drawer (3000) */
  pointer-events: auto;
  display: flex;
  justify-content: center;
  align-items: center;
}

.kv-ve-mobile-fab {
  width: 56px;
  height: 56px;
  border-radius: 999px;
  border: 1px solid #e5e7eb;
  background: rgba(255, 255, 255, 0.96);
  box-shadow: 0 18px 40px rgba(16, 24, 40, 0.18);
  backdrop-filter: blur(10px);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #344054;
}
.kv-ve-mobile-fab .mdi {
  font-size: 24px;
}
.kv-ve-mobile-fab--active {
  border-color: rgba(42, 170, 76, 0.45);
  color: #027a48;
}
.kv-ve-mobile-fab--cancel {
  border-color: rgba(240, 68, 56, 0.22);
  background: rgba(255, 255, 255, 0.96);
  color: #b42318;
}

.kv-ve-mobile-mini {
  display: flex;
  gap: 10px;
}

.kv-ve-mobile-bar {
  width: 100%;
}

.kv-ve-mobile-bar__inner {
  display: flex;
  flex-direction: column;
  padding: 0;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.96);
  border: 1px solid #e5e7eb;
  box-shadow: 0 18px 40px rgba(16, 24, 40, 0.18);
  backdrop-filter: blur(10px);
  height: 200px;
  align-items: stretch;
  overflow: hidden;
}

.kv-ve-mobile-bar__top {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  background: rgba(242, 244, 247, 0.95); /* section contrast */
  border-bottom: 1px solid #e5e7eb;
}

.kv-ve-mobile-bar__mode-group {
  display: inline-flex;
  gap: 6px;
  margin: 0 auto;
}

.kv-ve-mobile-bar__mode-btn {
  width: 30px;
  height: 30px;
  border-radius: 10px;
  border: 1px solid rgba(16, 24, 40, 0.06);
  background: rgba(255, 255, 255, 0.7);
  color: #667085;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.kv-ve-mobile-bar__mode-btn .mdi {
  font-size: 16px;
}
.kv-ve-mobile-bar__mode-btn.active {
  background: rgba(42, 170, 76, 0.12);
  border-color: rgba(42, 170, 76, 0.35);
  color: #027a48;
}
.kv-ve-mobile-bar__mode-btn--cancel {
  background: rgba(240, 68, 56, 0.10);
  border-color: rgba(240, 68, 56, 0.22);
  color: #b42318;
}
.kv-ve-mobile-bar__mode-btn--back {
  background: rgba(16, 24, 40, 0.06);
  border-color: rgba(16, 24, 40, 0.10);
  color: #344054;
}
.kv-ve-mobile-bar__mode-btn--close {
  background: rgba(16, 24, 40, 0.06);
  border-color: rgba(16, 24, 40, 0.10);
  color: #344054;
}

.kv-ve-mobile-bar__content {
  min-width: 0;
  display: flex;
  align-items: center;
  height: 100%;
  background: rgba(255, 255, 255, 0.96);
  padding: 10px;
}

.kv-ve-mobile-bar__icons {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: center;
}
.kv-ve-mobile-bar__icons--layout {
  gap: 6px;
}

.kv-ve-mobile-bar__icon-btn {
  width: 42px;
  height: 42px;
  border-radius: 14px;
  border: 1px solid #e5e7eb;
  background: #fff;
  color: #344054;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.kv-ve-mobile-bar__icon-btn .mdi {
  font-size: 20px;
}
.kv-ve-mobile-bar__icon-btn.active {
  border-color: rgba(42, 170, 76, 0.7);
  box-shadow: 0 0 0 4px rgba(42, 170, 76, 0.12);
  color: #027a48;
}

.kv-ve-mobile-bar__row-caption {
  font-size: 12px;
  font-weight: 700;
}

.kv-ve-mobile-bar__modules {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: stretch;
}
.kv-ve-mobile-bar__modules-empty {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #667085;
  font-size: 13px;
}
.kv-ve-mobile-bar__modules-scroll {
  width: 100%;
  display: flex;
  gap: 10px;
  overflow-x: auto;
  overflow-y: hidden;
  padding: 2px 2px;
  -webkit-overflow-scrolling: touch;
}

.kv-ve-mobile-bar__module-card {
  flex: 0 0 120px;
  border-radius: 14px;
  border: 1px solid #e5e7eb;
  background: #fff;
  padding: 8px;
  text-align: left;
}
.kv-ve-mobile-bar__module-card.active {
  border-color: rgba(42, 170, 76, 0.7);
  box-shadow: 0 0 0 4px rgba(42, 170, 76, 0.12);
}
.kv-ve-mobile-bar__module-thumb {
  height: 72px;
  border-radius: 10px;
  background: #f2f4f7;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}
.kv-ve-mobile-bar__module-thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.kv-ve-mobile-bar__module-thumb-fallback {
  font-size: 11px;
  color: #667085;
}
.kv-ve-mobile-bar__module-name {
  margin-top: 6px;
  font-size: 11px;
  color: #101828;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>


